// Client access to the recalibration feature flags. Flags are global (not
// per-user): an `appConfig/featureFlags` Firestore doc holds the overrides,
// merged over RECALIBRATION_FLAG_DEFAULTS. Edited from the admin portal via
// the `setFeatureFlags` callable.

import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db } from './firebase';
import {
  FEATURE_FLAGS_DOC,
  resolveFeatureFlags,
  type RecalibrationFlagKey,
} from '../constants/feature-flags';

export type FeatureFlagMap = Record<RecalibrationFlagKey, boolean>;
type FlagOverrides = Partial<Record<RecalibrationFlagKey, boolean>>;

function flagDocRef() {
  return doc(db, FEATURE_FLAGS_DOC.collection, FEATURE_FLAGS_DOC.doc);
}

function overridesFromSnap(data: Record<string, unknown> | undefined): FlagOverrides {
  // Doc shape: { flags: { 'recal.coach': true, ... } }
  return (data?.flags ?? {}) as FlagOverrides;
}

/** One-shot fetch of the effective flag map. */
export async function getFeatureFlags(): Promise<FeatureFlagMap> {
  try {
    const snap = await getDoc(flagDocRef());
    return resolveFeatureFlags(overridesFromSnap(snap.data()));
  } catch {
    return resolveFeatureFlags(null);
  }
}

/** Live subscription to the effective flag map. Returns an unsubscribe fn. */
export function onFeatureFlagsChange(cb: (flags: FeatureFlagMap) => void): () => void {
  return onSnapshot(
    flagDocRef(),
    (snap) => cb(resolveFeatureFlags(overridesFromSnap(snap.data()))),
    () => cb(resolveFeatureFlags(null)),
  );
}

let _functions: ReturnType<typeof import('firebase/functions').getFunctions> | null = null;
async function getFunctionsInstance() {
  if (!_functions) {
    const { getFunctions } = await import('firebase/functions');
    const { app } = await import('./firebase');
    _functions = getFunctions(app);
  }
  return _functions;
}

/** Admin-only: persist flag overrides. */
export async function setFeatureFlags(updates: FlagOverrides): Promise<{ ok: true }> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable<{ updates: FlagOverrides }, { ok: true }>(functions, 'setFeatureFlags');
  const result = await fn({ updates });
  return result.data;
}
